import { useCallback, useMemo } from 'react'

import type { TokenImageSlug } from '../../components/TokenImage'
import { useGetWalletBalance } from '../blockchain/useGetWalletBalance'
import { useGetTokenPrice } from '../fission_dex'
import { amount_to_tokens } from '../helpers/block_helpers'
import { useGetSymbolToDecimal } from '../portfolio'

import {
  useGetVaultBalances,
  useGetVaultTotalTokens,
  type VaultBalances,
} from './vaults_block_queries'
import {
  calc_token_returns,
  find_deposits_from_deposit_total,
  find_max_deposits,
} from './vaults_logic'

export function useGetVaultDepositValues({
  vault_id: id,
  deposit_total_usdc_value,
}: {
  vault_id: RubyID
  deposit_total_usdc_value: number
}) {
  const symbol = 'TECH'
  const { data: balances } = useGetVaultBalances({ id })
  const { data: fund_price_per_token } = useGetTokenPrice({ symbol })
  const symbol_to_decimal = useGetSymbolToDecimal()

  const enabled =
    balances != null &&
    symbol_to_decimal != null &&
    fund_price_per_token != null

  const re_compute = useCallback(
    (total: number) => {
      if (!enabled) {
        return {
          deposit_usdc_tokens: 0,
          deposit_fund_tokens: 0,
        }
      }

      return find_deposits_from_deposit_total({
        deposit_total_usdc_value: total,
        fund_price_per_token,
        fund_decimal: symbol_to_decimal[symbol],
        usdc_decimal: symbol_to_decimal['USDC'],
        balances,
      })
    },
    [balances, enabled, fund_price_per_token, symbol_to_decimal],
  )

  return {
    re_compute,
    enabled,
    ...re_compute(deposit_total_usdc_value),
  }
}

export function useGetVaultWithdrawValues({
  id,
  redeem_vault_tokens,
}: {
  id: RubyID | undefined
  redeem_vault_tokens: number
}) {
  const symbol = 'TECH'
  const { data: balances } = useGetVaultBalances({ id })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const { data: total_vault_tokens } = useGetVaultTotalTokens({ id })

  const enabled =
    balances != null && symbol_to_decimal != null && total_vault_tokens != null

  const values = useMemo(() => {
    if (!enabled) {
      return {
        returned_usdc_tokens: 0,
        returned_fund_tokens: 0,
      }
    }

    return calc_token_returns({
      redeem_vault_tokens,
      total_vault_tokens,
      fund_decimal: symbol_to_decimal[symbol],
      usdc_decimal: symbol_to_decimal['USDC'],
      balances,
    })
  }, [
    enabled,
    redeem_vault_tokens,
    total_vault_tokens,
    symbol_to_decimal,
    balances,
  ])

  return {
    enabled,
    ...values,
  }
}

export type VaultCompositionType = {
  name: 'TECH' | 'USDC' | 'M^0' | 'USDC ⇄ TECH'
  long_name: string
  usdc_value: number
  percentage: number
  tokens: number
} & (
  | {
      type: 'token'
      image_slug: TokenImageSlug
    }
  | {
      type: 'lp'
      image_slug: [TokenImageSlug, TokenImageSlug]
    }
)

type symbol_to_decimal_type = NonNullable<
  ReturnType<typeof useGetSymbolToDecimal>
>

function balance_to_tokens({
  balances,
  symbol_to_decimal,
  symbol,
}: {
  balances: VaultBalances
  symbol: keyof symbol_to_decimal_type
  symbol_to_decimal: symbol_to_decimal_type
}) {
  return {
    usdc_tokens: amount_to_tokens(
      balances?.usdc_balance,
      symbol_to_decimal['USDC'],
    ),
    fund_tokens: amount_to_tokens(
      balances?.fund_balance,
      symbol_to_decimal[symbol],
    ),
    usdc_positions_tokens: amount_to_tokens(
      balances?.usdc_auto_position_amount +
        balances?.usdc_manual_position_amount,
      symbol_to_decimal['USDC'],
    ),
    fund_positions_tokens: amount_to_tokens(
      balances?.fund_auto_position_amount +
        balances?.fund_manual_position_amount,
      symbol_to_decimal[symbol],
    ),
  }
}

function count_lp_tokens({ balances }: { balances: VaultBalances }) {
  let lp_tokens = 0
  if (
    balances?.fund_manual_position_amount > 0 ||
    balances?.usdc_manual_position_amount > 0
  ) {
    lp_tokens += 1
  }
  if (
    balances?.fund_auto_position_amount > 0 ||
    balances?.usdc_auto_position_amount > 0
  ) {
    lp_tokens += 1
  }
  return lp_tokens
}

export function useGetVaultComposition({ id }: { id: RubyID | undefined }) {
  const symbol = 'TECH'
  const { data: fund_price_per_token } = useGetTokenPrice({ symbol })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const { data: balances } = useGetVaultBalances({ id })

  const enabled =
    id != null &&
    symbol_to_decimal != null &&
    fund_price_per_token != null &&
    balances != null

  return useMemo(() => {
    if (!enabled) {
      return {
        enabled: false,
        tvl: 0,
        composition: [],
      }
    }
    const {
      usdc_tokens,
      fund_tokens,
      usdc_positions_tokens,
      fund_positions_tokens,
    } = balance_to_tokens({ balances, symbol_to_decimal, symbol })

    const composition: Omit<VaultCompositionType, 'percentage'>[] = [
      {
        type: 'token',
        image_slug: 'technology',
        name: 'TECH',
        long_name: 'Fission Technology Holdings',
        tokens: fund_tokens,
        usdc_value: fund_price_per_token * fund_tokens,
      },
      {
        type: 'token',
        image_slug: 'usdc',
        name: 'USDC',
        long_name: 'USD Coin',
        tokens: usdc_tokens,
        usdc_value: usdc_tokens,
      },
      {
        type: 'lp',
        image_slug: ['technology', 'usdc'],
        name: 'USDC ⇄ TECH',
        long_name: 'USDC ⇄ TECH LP Token',
        tokens: count_lp_tokens({ balances }),
        usdc_value:
          fund_positions_tokens * fund_price_per_token + usdc_positions_tokens,
      },
    ]

    const tvl = composition.reduce((acc, item) => acc + item.usdc_value, 0)

    return {
      enabled: true,
      tvl,
      composition: composition.map(
        (item) =>
          ({
            ...item,
            percentage:
              (tvl > 0 ? item.usdc_value / tvl : 1 / composition.length) * 100,
          }) as VaultCompositionType,
      ),
    }
  }, [balances, enabled, fund_price_per_token, symbol_to_decimal])
}

export function useMyGetMaxDepositValues({ id }: { id: RubyID }): {
  enabled: boolean
  max_deposit_usdc_tokens: number
  max_deposit_fund_tokens: number
  balance_fund_tokens: number
  balance_usdc_tokens: number
} {
  const symbol = 'TECH'
  const { balance: balance_fund_tokens, isConnected } =
    useGetWalletBalance(symbol)
  const { balance: balance_usdc_tokens } = useGetWalletBalance('USDC')

  const { data: balances } = useGetVaultBalances({ id })
  const symbol_to_decimal = useGetSymbolToDecimal()

  const enabled = balances != null && symbol_to_decimal && isConnected

  return useMemo(() => {
    if (!enabled) {
      return {
        enabled: false,
        max_deposit_usdc_tokens: 0,
        max_deposit_fund_tokens: 0,
        balance_usdc_tokens,
        balance_fund_tokens,
      }
    }

    const max = find_max_deposits({
      max_deposit_usdc_tokens: balance_usdc_tokens,
      max_deposit_fund_tokens: balance_fund_tokens,
      fund_decimal: symbol_to_decimal[symbol],
      usdc_decimal: symbol_to_decimal['USDC'],
      balances,
    })

    return {
      enabled: true,
      max_deposit_usdc_tokens: max.deposit_usdc_tokens,
      max_deposit_fund_tokens: max.deposit_fund_tokens,
      balance_usdc_tokens,
      balance_fund_tokens,
    }
  }, [
    balance_fund_tokens,
    balance_usdc_tokens,
    balances,
    enabled,
    symbol_to_decimal,
  ])
}

export function useGetVaultPricePerToken({ id }: { id: RubyID | undefined }) {
  const composition_result = useGetVaultComposition({ id })
  const { data: total_tokens } = useGetVaultTotalTokens({ id })

  const enabled = composition_result.enabled && total_tokens != null

  return useMemo(() => {
    if (!enabled) {
      return {
        enabled: false,
        vault_price_per_token: undefined,
      } as const
    }

    return {
      enabled: true,
      vault_price_per_token: composition_result.tvl / total_tokens,
    } as const
  }, [composition_result.tvl, enabled, total_tokens])
}
